import React, { useState, useCallback } from 'react';
import Cropper, { Area, Point } from 'react-easy-crop';
import { X, ZoomIn, ZoomOut, Check, Crop } from 'lucide-react';

interface ImageCropModalProps {
  isOpen: boolean;
  imageSrc: string | null;
  aspect?: number;
  aspectLabel?: string;
  title: string;
  recommendedText?: string;
  onClose: () => void;
  onConfirm: (croppedFile: File, previewUrl: string) => void;
}

const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    if (!url.startsWith('data:') && !url.startsWith('blob:')) {
      image.setAttribute('crossOrigin', 'anonymous');
    }
    image.src = url;
  });

async function getCroppedImg(
  imageSrc: string,
  pixelCrop: Area,
  fileName: string = 'cropped-image.jpg'
): Promise<File> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Unable to create canvas context');
  }

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Canvas conversion failed'));
          return;
        }
        const file = new File([blob], fileName, { type: 'image/jpeg' });
        resolve(file);
      },
      'image/jpeg',
      0.95
    );
  });
}

export default function ImageCropModal({
  isOpen,
  imageSrc,
  aspect = 16 / 9,
  aspectLabel,
  title,
  recommendedText,
  onClose,
  onConfirm,
}: ImageCropModalProps) {
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState<number>(1);
  const [currentAspect, setCurrentAspect] = useState<number | undefined>(aspect);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [processing, setProcessing] = useState(false);

  React.useEffect(() => {
    if (isOpen) {
      setCurrentAspect(aspect);
      setZoom(1);
      setCrop({ x: 0, y: 0 });
    }
  }, [aspect, isOpen]);

  const onCropComplete = useCallback((_croppedArea: Area, currentCroppedAreaPixels: Area) => {
    setCroppedAreaPixels(currentCroppedAreaPixels);
  }, []);

  const handleConfirm = async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    setProcessing(true);
    try {
      const croppedFile = await getCroppedImg(imageSrc, croppedAreaPixels, `cropped-${Date.now()}.jpg`);
      const previewUrl = URL.createObjectURL(croppedFile);
      onConfirm(croppedFile, previewUrl);
      onClose();
    } catch (e) {
      console.error('Failed to crop image', e);
      alert('Could not crop image. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  if (!isOpen || !imageSrc) return null;

  const defaultLabel = aspectLabel || (
    aspect === 5 ? '5:1 (Page Banner)' :
    aspect === 4.5 ? '4.5:1 (Page Banner)' :
    aspect === 3.5 ? '3.5:1 (Panoramic)' :
    aspect === 2 ? '2:1 (Slider Banner)' :
    Math.abs(aspect - 4 / 3) < 0.05 ? '4:3 (Home Card)' :
    aspect === 1 ? '1:1 (Square)' :
    Math.abs(aspect - 16 / 9) < 0.05 ? '16:9 (Widescreen)' :
    `${aspect}:1 (Auto)`
  );

  const isAutoSelected = currentAspect !== undefined;

  return (
    <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/80 backdrop-blur-sm p-3 sm:p-4">
      <div className="bg-[#0f172a] border border-gray-800 rounded-xl w-full max-w-2xl overflow-hidden shadow-2xl flex flex-col max-h-[92vh]">
        {/* Compact Header with Inline Mode Switcher */}
        <div className="px-4 py-2.5 bg-slate-900 border-b border-gray-800 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
            <div className="flex items-center gap-1.5 text-white font-bold text-xs shrink-0 mr-1">
              <Crop className="w-4 h-4 text-purple-400" />
              <span>{title}</span>
            </div>

            <div className="flex items-center gap-1.5 shrink-0">
              <button
                type="button"
                onClick={() => setCurrentAspect(aspect)}
                className={`px-2.5 py-1 rounded-md text-[11px] font-bold transition-all flex items-center gap-1 ${
                  isAutoSelected
                    ? 'bg-purple-600 text-white shadow'
                    : 'bg-slate-800 text-slate-400 hover:text-white border border-slate-700'
                }`}
              >
                <span>★</span>
                {defaultLabel}
              </button>

              <button
                type="button"
                onClick={() => setCurrentAspect(undefined)}
                className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all ${
                  !isAutoSelected
                    ? 'bg-purple-600 text-white shadow'
                    : 'bg-slate-800 text-slate-400 hover:text-white border border-slate-700'
                }`}
              >
                Free Crop
              </button>
            </div>
          </div>

          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white p-1 rounded-lg hover:bg-gray-800 transition shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Cropper Container - Compact height */}
        <div className="relative w-full h-64 sm:h-72 bg-black/95">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={currentAspect}
            minZoom={0.5}
            maxZoom={3}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            showGrid={true}
            zoomWithScroll={true}
          />
        </div>

        {/* Compact Single-Row Controls & Footer */}
        <div className="px-4 py-2.5 bg-[#090d16] border-t border-gray-800 flex flex-wrap items-center justify-between gap-2.5">
          {/* Zoom controls */}
          <div className="flex items-center gap-2 flex-1 min-w-[240px]">
            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider shrink-0 flex items-center gap-1">
              <ZoomIn className="w-3.5 h-3.5 text-purple-400" />
              Zoom:
            </span>
            <button
              type="button"
              onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.1).toFixed(2)))}
              className="w-6 h-6 rounded bg-slate-800 hover:bg-slate-700 text-white flex items-center justify-center font-bold text-sm transition border border-slate-700 shadow-sm"
              title="Zoom Out"
            >
              −
            </button>
            <div className="flex-1 flex items-center max-w-[120px] sm:max-w-[150px]">
              <input
                type="range"
                value={zoom}
                min={0.5}
                max={3}
                step={0.05}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="w-full h-1.5 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-purple-500 hover:accent-purple-400"
              />
            </div>
            <button
              type="button"
              onClick={() => setZoom((z) => Math.min(3, +(z + 0.1).toFixed(2)))}
              className="w-6 h-6 rounded bg-slate-800 hover:bg-slate-700 text-white flex items-center justify-center font-bold text-sm transition border border-slate-700 shadow-sm"
              title="Zoom In"
            >
              +
            </button>
            <span className="text-[11px] font-mono font-bold text-purple-300 w-11 text-center bg-purple-950/70 px-1.5 py-0.5 rounded border border-purple-800/50 shrink-0">
              {Math.round(zoom * 100)}%
            </span>
            <button
              type="button"
              onClick={() => { setZoom(1); setCrop({ x: 0, y: 0 }); }}
              className="text-[10px] font-semibold text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-750 px-2 py-0.5 rounded border border-slate-700 transition shrink-0"
            >
              Reset
            </button>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={onClose}
              disabled={processing}
              className="px-3 py-1.5 text-xs font-semibold text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={processing}
              className="flex items-center gap-1.5 px-4 py-1.5 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-xs font-bold shadow-md shadow-purple-600/30 transition disabled:opacity-50"
            >
              <Check className="w-3.5 h-3.5" />
              {processing ? 'Processing...' : 'Crop & Use Image'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
